/**
 * CrossTwinInsightsCard — iter630 — Знаниева мрежа между всичките 16 близнаци.
 * Показва активни физиологични връзки според текущите sub_scores.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { ChevronDown, ChevronUp, Activity, AlertTriangle, Info, Sparkles } from "lucide-react";

const SEVERITY_CONFIG = {
  action: { icon: AlertTriangle, label: "ДЕЙСТВИЕ", className: "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-300" },
  watch:  { icon: Activity, label: "НАБЛЮДЕНИЕ", className: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300" },
  info:   { icon: Info, label: "ИНФО", className: "border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300" },
};

const TWIN_LABEL_BG = {
  nutritional: "Хранителен", cognitive: "Когнитивен", lab: "Лабораторен",
  phenotype: "Фенотип", microbiome: "Микробиом", body_comp: "Телесен",
  sleep: "Сън", stress: "Стрес", thyroid: "Щитовидна",
  cardio: "Кардио", hormonal: "Хормонален", metabolic_insulin: "Метаболитен",
  inflammation: "Възпаление", movement: "Движение",
  mental_emotional: "Психо-емоц.", liver_kidney: "Черен дроб", dental: "Дентален",
  kidney: "Бъбреци (KHI)", bhi: "Гърда (BHI)", male_health: "Мъжко здраве",
};

export default function CrossTwinInsightsCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // per-insight expand
  const [showAll, setShowAll] = useState(false);
  const [aiOn, setAiOn] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async (withAi = false, fresh = false) => {
    setLoading(true);
    try {
      const params = [];
      if (withAi) params.push("ai_enrich=true");
      if (fresh) params.push("nocache=true");
      const qs = params.length ? "?" + params.join("&") : "";
      const { data } = await axios.get(`${API}/cross-twin-insights/active${qs}`, { timeout: withAi ? 45000 : 20000 });
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); setAiLoading(false); }
  };

  const toggleAi = async () => {
    const next = !aiOn;
    setAiOn(next);
    if (next) {
      setAiLoading(true);
      await load(true);
    } else {
      await load(false);
    }
  };

  useEffect(() => {
    load();
    const onChange = () => load(aiOn, true);  // fresh=true on user events
    ["meal-logged", "meal-added", "meal-log-changed", "thyroid-lab-changed",
     "stress-log-changed", "hereditary-risk-changed", "cycle-changed"].forEach(e =>
      window.addEventListener(e, onChange));
    return () => {
      ["meal-logged", "meal-added", "meal-log-changed", "thyroid-lab-changed",
       "stress-log-changed", "hereditary-risk-changed", "cycle-changed"].forEach(e =>
        window.removeEventListener(e, onChange));
    };
    // eslint-disable-next-line
  }, [API]);

  if (loading || !data) return null;
  if (data.active_count === 0) {
    return (
      <div className={`mt-3 p-3 rounded-xl border ${isDark ? "bg-emerald-900/20 border-emerald-700/30" : "bg-emerald-50 border-emerald-200"}`} data-testid="cross-twin-empty">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <div className="text-[12px]">
            <strong className="text-[11px] uppercase tracking-wider opacity-80">Физиологична мрежа</strong>
            <p className="opacity-90">Няма активни патофизиологични връзки за момента. Системите работят синхронно.</p>
          </div>
        </div>
      </div>
    );
  }

  const visible = showAll ? data.active : data.active.slice(0, 3);

  return (
    <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-slate-900/40 border border-slate-800" : "bg-white/80 border border-slate-200"}`} data-testid="cross-twin-insights">
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[10px] uppercase font-bold ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`}>
          🧬 Физиологична мрежа · {data.active_count} активни връзки
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAi}
            disabled={aiLoading}
            data-testid="cross-twin-ai-toggle"
            className={`text-[10px] px-2 py-1 rounded-md font-bold transition-colors ${
              aiOn
                ? "bg-fuchsia-500 text-white"
                : isDark ? "bg-slate-700/60 hover:bg-slate-700 text-fuchsia-300" : "bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-700"
            } disabled:opacity-50`}
          >
            {aiLoading ? "⏳ AI..." : aiOn ? "✨ AI вкл." : "✨ AI"}
          </button>
          <span className="text-[10px] opacity-60">
            {data.total_rules} правила
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {visible.map((insight, i) => {
          const Sev = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
          const IconC = Sev.icon;
          const isExp = !!expanded[insight.id];
          return (
            <div
              key={insight.id}
              className={`p-3 rounded-lg border-2 ${Sev.className}`}
              data-testid={`insight-${insight.id}`}
            >
              <button onClick={() => setExpanded(s => ({ ...s, [insight.id]: !s[insight.id] }))}
                      className="w-full text-left"
                      data-testid={`insight-toggle-${insight.id}`}>
                <div className="flex items-start gap-2">
                  <IconC className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[9px] font-bold tracking-wider opacity-80">{Sev.label}</span>
                      <span className="text-[9px] opacity-60">·</span>
                      <span className="text-[9px] uppercase opacity-70">{insight.category_bg}</span>
                    </div>
                    <div className="text-[12px] font-semibold leading-snug">{insight.title_bg}</div>
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {(insight.twins || []).map(t => (
                        <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? "bg-slate-700/60" : "bg-white/80 border border-slate-200"}`}>
                          {TWIN_LABEL_BG[t] || t}
                        </span>
                      ))}
                    </div>
                  </div>
                  {isExp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </div>
              </button>
              {isExp && (
                <div className="mt-2.5 pl-6 space-y-1.5" data-testid={`insight-details-${insight.id}`}>
                  {insight.message_bg_ai ? (
                    <>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[9px] uppercase font-bold tracking-wider opacity-70">✨ Персонализирано от AI</span>
                      </div>
                      <p className="text-[11px] leading-relaxed font-medium" data-testid={`insight-ai-${insight.id}`}>{insight.message_bg_ai}</p>
                      <details className="text-[10px] opacity-60 hover:opacity-100 cursor-pointer">
                        <summary>Виж оригиналното обяснение</summary>
                        <p className="mt-1 italic">{insight.message_bg}</p>
                      </details>
                    </>
                  ) : (
                    <p className="text-[11px] leading-relaxed">{insight.message_bg}</p>
                  )}
                  <p className="text-[10px] italic opacity-70">📚 {insight.evidence_bg}</p>
                  <div className={`p-2 rounded text-[11px] ${isDark ? "bg-slate-900/60" : "bg-slate-50"}`}>
                    <strong className="text-[9px] uppercase tracking-wider opacity-70 block mb-0.5">Действие</strong>
                    {insight.action_bg}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.active_count > 3 && (
        <button onClick={() => setShowAll(s => !s)}
                className={`mt-2 w-full text-[11px] py-1.5 rounded-lg ${isDark ? "bg-slate-800/60 hover:bg-slate-800" : "bg-slate-100 hover:bg-slate-200"}`}
                data-testid="insights-show-all">
          {showAll ? `Покажи само топ 3` : `Покажи всички ${data.active_count} връзки`}
        </button>
      )}
    </div>
  );
}
